import React, { useState, useMemo, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import {
  BUTTON_VARIANTS,
  Dialog,
  SecondaryButton,
  Loader,
  TOAST_POSITIONS, 
  TOAST_VARIANTS,
  MultiToasts,
  TOAST_VISIBILITY_DURATIONS
} from "cdk-radial";
import { useHistory } from "react-router-dom";
import "./styles.css";

import { withAuth } from "@cdk-prod/fortellis-auth-context";
import WithNavigationToHome from "../components/WithNavigationToHome";
import config from "../config/app.conf.json";
import jwt_decode from "jwt-decode"; 
import UploadCSVFiles from "./UploadCSVFiles";
import {CSV_FILE} from '../../src/Utils/constants'
import DownloadCSVFile from "./DownloadCSVFile";

function FeeWaiverManagement(props) {
  const { 
    FeeWaiverManagementReducer, 
    resetToInitialState, 
    auth,
    downloadIntegrationCSV 
  } = props;
  const {
    isSubmittedAppApiIntegrationCSV,
    isSubmittedSubscriptionOrganisationCSV,
    uploadAppApiIntegrationCSVError,
    uploadSubscriptionOrganisationCSVError,
    uploadingAppApiIntegration,
    uploadingSubscriptionOrganisation,
    downloadingIntegrationCSV,
  } = FeeWaiverManagementReducer;

  const history = useHistory(); 
  const [showPermissionPopup, setShowPermissionPopup] = useState(false);

  const [toasts, setToasts] = useState([]);

  const addToast = toast => setToasts(prev => {
    return [toast, ...prev]
  });
  const removeToast = id => setToasts(prev => prev.filter(toast => toast.id !== id));
  const createSucessToast = ({fileName, message}) => {
    const id = `id-success-${fileName}`;
    return {
      actions: [
        {
          onClick: () => removeToast(id),
          text: "Dismiss",
        },
      ],
      content: <span>{message}</span>,
      id,
      variant: TOAST_VARIANTS.POSITIVE,
      visibilityDuration: TOAST_VISIBILITY_DURATIONS.INFINITE,
    };
  };

  const createFailureToast = ({fileName, message}) => {
    const id = `id-failure-${fileName}`;
    return {
      actions: [
        {
          onClick: () => removeToast(id),
          text: "Dismiss",
        },
      ],
      content: formatNotificationMessage(message, fileName),
      id,
      variant: TOAST_VARIANTS.NEGATIVE,
      visibilityDuration: TOAST_VISIBILITY_DURATIONS.INFINITE
    };
  };

  const authToken = auth.accessToken;
  const decodedToken = useMemo(() => authToken ? jwt_decode(authToken) : null, [authToken]);

  const hasManageFeeWaiverPermission = useMemo(() => {
    if (!decodedToken?.entitlements) return false;
    const FeeWaiverMgmtPermissions = decodedToken.entitlements["*"]?.["fee-waiver-mgmt"];
    return Array.isArray(FeeWaiverMgmtPermissions) && FeeWaiverMgmtPermissions.includes("manage");
  }, [decodedToken]);

  const formatNotificationMessage = (message, fileName) => {
    const file = fileName === CSV_FILE.APP_API_INTEGRATION_ID ? CSV_FILE.APP_API_INTEGRATION_FILE_NAME : CSV_FILE.SUBSCRIBER_ORGANIZATION_FILE_NAME;
    const mapLines = message.replaceAll('No.', 'number').split('.');
    return (
      <div>
        <div>
          {file} file upload failed :
        </div>
        <ol>
          {mapLines.map(error => {
            return <li>{`${error}.`}</li>
          })}
        </ol>
      </div>
    ) 
  }

  // success toast
  useEffect(() => {
    if(isSubmittedAppApiIntegrationCSV && !toasts.some(toast => toast.id === `id-success-${CSV_FILE.APP_API_INTEGRATION_ID}`)) {
      if(toasts.some(toast => toast.id === `id-failure-${CSV_FILE.APP_API_INTEGRATION_ID}`)){
        removeToast(`id-failure-${CSV_FILE.APP_API_INTEGRATION_ID}`);
      }
      addToast(createSucessToast({fileName: CSV_FILE.APP_API_INTEGRATION_ID, message: `${CSV_FILE.APP_API_INTEGRATION_FILE_NAME} file upload successful.`}))
    }
  }, [isSubmittedAppApiIntegrationCSV])

  useEffect(() => {
    if(isSubmittedSubscriptionOrganisationCSV && !toasts.some(toast => toast.id === `id-success-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`)) {
      if(toasts.some(toast => toast.id === `id-failure-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`)){
        removeToast(`id-failure-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`);
      }
      addToast(createSucessToast({fileName: CSV_FILE.SUBSCRIBER_ORGANIZATION_ID, message: `${CSV_FILE.SUBSCRIBER_ORGANIZATION_FILE_NAME} file upload successful.`}))
    }
  }, [isSubmittedSubscriptionOrganisationCSV])
  
  // failure toast
  useEffect(() => {
    if(uploadAppApiIntegrationCSVError && !toasts.some(toast => toast.id === `id-failure-${CSV_FILE.APP_API_INTEGRATION_ID}`)) {
      if(toasts.some(toast => toast.id === `id-success-${CSV_FILE.APP_API_INTEGRATION_ID}`)){
        removeToast(`id-success-${CSV_FILE.APP_API_INTEGRATION_ID}`);
      }
      addToast(createFailureToast({fileName: CSV_FILE.APP_API_INTEGRATION_ID, message: uploadAppApiIntegrationCSVError}))
    }
  }, [uploadAppApiIntegrationCSVError])

  useEffect(() => {
    if(uploadSubscriptionOrganisationCSVError && !toasts.some(toast => toast.id === `id-failure-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`)) {
      if(toasts.some(toast => toast.id === `id-success-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`)){
        removeToast(`id-success-${CSV_FILE.SUBSCRIBER_ORGANIZATION_ID}`);
      }
      addToast(createFailureToast({fileName: CSV_FILE.SUBSCRIBER_ORGANIZATION_ID, message: uploadSubscriptionOrganisationCSVError}))
    }
  }, [uploadSubscriptionOrganisationCSVError])

  useEffect(() => {
    if (!hasManageFeeWaiverPermission) {
      setShowPermissionPopup(true);
    }

    return () =>  resetToInitialState();
  }, [hasManageFeeWaiverPermission]);

  return config?.isFeeWaiverManagementEnabled ? (
    <div className="component-content">
      <div className="fm-page-content-grid max-width-resp page-padding-common">
        <div className="fee-waiver-page-title">
          <FormattedMessage
            id="Home.action.feeWaiverManagement"
            defaultMessage="Fee Waiver Management"
          />
        </div>
        {showPermissionPopup ? (
          <Dialog
            buttonsProps={[
              {
                id: "dialog-action-1",
                dataTestId: "backButton",
                onClick: () => history.push("/"),
                text: "Back",
                variant: BUTTON_VARIANTS.PRIMARY,
              },
            ]}
            dataTestId="permissionPopup"
            id="permission-dialog"
            title={"Permission Denied"}
            isOpen={true}
            className="bundle_share_dialog_box"
            hideHeader={true}
          >
            You don't have permission to view this page.
            <br />
            <br />
          </Dialog>
        ) : (
          <>
            <MultiToasts style={{maxWidth: '46rem'}} className='csv_notification' items={toasts} position={TOAST_POSITIONS.FIXED} />
            <div className="fee-waiver-main-container">
              <DownloadCSVFile
                downloadIntegrationCSV={downloadIntegrationCSV}
                accessToken={authToken}
              />
              <UploadCSVFiles {...props} />
            </div>
            {(downloadingIntegrationCSV) && (
              <div className="file-upload-loader">
                <Loader
                  label={
                    <span 
                    style={{ fontSize: "18px", fontWeight: "600" }}
                    >
                      Downloading file ...
                    </span>
                  }
                />
              </div>
            )}
            {(uploadingAppApiIntegration ||
              uploadingSubscriptionOrganisation) && (
              <div className="file-upload-loader">
                <Loader
                  label={
                    <span style={{ fontSize: "18px", fontWeight: "600" }}>
                      Uploading file ...
                    </span>
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  ) : null;
}
if (config?.isFeeWaiverManagementEnabled) {

FeeWaiverManagement = WithNavigationToHome(FeeWaiverManagement);
}

class FeeWaiverManagementContainer extends React.Component {
  render() {
    return this.props.auth.isAuthenticated ? (
      <FeeWaiverManagement {...this.props} />
    ) : (
      <div></div>
    );
  }
}

export default withAuth(FeeWaiverManagementContainer);
